import { createElement, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, FileBadge } from "lucide-react";

/**
 * RichMessage — rendu enrichi des reponses IA.
 * Gere : gras, italique, titres, listes, tableaux, separateurs,
 * pastilles de verdict, etiquettes de source depliables, surlignage des estimations,
 * mise en valeur des montants, et repli des reponses longues.
 */

const SRC_RE = /\[SOURCE(?:\s+WEB)?\s*:\s*([^\]]+)\]/gi;
const EST_RE = /\[ESTIMATION[^\]]*\]/gi;
const NUM_RE = /([+-]?\d[\d\s\u00a0]*(?:[.,]\d+)?\s?(?:EUR|€|%|m2|m²|EUR\/m2))/gi;
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)<>"]+)/g;

const CALLOUTS = [
  { re: /TON ANALYSE TERRAIN/i, tone: "terrain" },
  { re: /ETAPE SUIVANTE AVEC L'?EQUIPE|ÉTAPE SUIVANTE AVEC L'?ÉQUIPE/i, tone: "equipe" },
  { re: /CONTROLE DE COHERENCE|CONTRÔLE DE COHÉRENCE/i, tone: "controle" },
  { re: /CONDITIONS? BLOQUANTES?|POINTS? DE VIGILANCE|RISQUES?/i, tone: "alerte" },
];

const VERDICTS = [
  { re: /^GO SOUS CONDITIONS?/i, tone: "warn" },
  { re: /^GO\b/i, tone: "ok" },
  { re: /^ATTENDS?\b/i, tone: "warn" },
  { re: /^NEXT\b/i, tone: "bad" },
];

function calloutTone(titre) {
  const found = CALLOUTS.find((c) => c.re.test(titre));
  return found ? found.tone : null;
}

/** Extrait les etiquettes [SOURCE : ...] du texte et les remplace par un renvoi numerote. */
function extractSources(text) {
  const sources = [];
  const clean = text.replace(SRC_RE, (_m, contenu) => {
    const label = contenu.trim().replace(/\s+/g, " ");
    let idx = sources.indexOf(label);
    if (idx === -1) {
      sources.push(label);
      idx = sources.length - 1;
    }
    return `\u0001${idx + 1}\u0001`;
  });
  return { clean, sources };
}

/** Tokenise une ligne : gras, italique, montants, estimations, liens, renvois de source. */
function inline(text, key, onSourceClick) {
  if (!text) return null;
  const out = [];
  let rest = text;
  let n = 0;
  const pattern = new RegExp(
    [
      "\\*\\*([^*]+)\\*\\*",
      "__([^_]+)__",
      "\\*([^*\\n]+)\\*",
      "\u0001(\\d+)\u0001",
      EST_RE.source,
      LINK_RE.source,
      NUM_RE.source,
    ].join("|"),
    "gi"
  );
  let last = 0;
  let m;
  while ((m = pattern.exec(rest)) !== null) {
    if (m.index > last) out.push(rest.slice(last, m.index));
    const k = `${key}-${n++}`;
    if (m[1] || m[2]) {
      out.push(<strong key={k} className="rm-b">{m[1] || m[2]}</strong>);
    } else if (m[3]) {
      out.push(<em key={k}>{m[3]}</em>);
    } else if (m[4]) {
      out.push(
        <button
          key={k}
          type="button"
          className="rm-src-ref"
          onClick={onSourceClick}
          title="Voir les sources"
        >
          {m[4]}
        </button>
      );
    } else if (/^\[ESTIMATION/i.test(m[0])) {
      out.push(<mark key={k} className="rm-est">estimation a recouper</mark>);
    } else if (/^https?:\/\//i.test(m[0]) || /^\[[^\]]+\]\(/.test(m[0])) {
      const url = m[0].startsWith("[") ? m[0].match(/\((https?:\/\/[^\s)]+)\)/)[1] : m[0];
      const label = m[0].startsWith("[")
        ? m[0].slice(1, m[0].indexOf("]"))
        : url.replace(/^https?:\/\//, "").split("/")[0];
      out.push(
        <a key={k} href={url} target="_blank" rel="noopener noreferrer" className="rm-link">
          {label}
        </a>
      );
    } else {
      out.push(<span key={k} className="rm-num">{m[0]}</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < rest.length) out.push(rest.slice(last));
  return out.length ? out : text;
}

/** Une ligne qui ne contient plus qu'un renvoi de source n'a rien a afficher. */
function videHorsSources(t) {
  return !t || t.replace(/\u0001\d+\u0001/g, "").replace(/[\s:.,;-]/g, "") === "";
}

function isTableRow(l) {
  return /^\s*\|.*\|\s*$/.test(l);
}
function isSep(l) {
  return /^\s*\|?[\s:-]*-{2,}[\s:|-]*$/.test(l);
}
function splitRow(l) {
  return l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}
function isBullet(l) {
  return /^\s*([-•*]|\d+[.)])\s+/.test(l);
}
function isSubBullet(l) {
  return /^\s{2,}[.·]\s+/.test(l);
}
function headingLevel(l) {
  const md = l.match(/^(#{1,6})\s+(.*)$/);
  if (md) return { level: Math.min(md[1].length + 1, 4), titre: md[2].trim() };
  const t = l.trim();
  // "TITRE DE SECTION :" en majuscules = titre (convention des agents)
  if (
    t.length > 2 &&
    t.length < 90 &&
    t === t.toUpperCase() &&
    /[A-Z]{3}/.test(t) &&
    !isBullet(t) &&
    !isTableRow(t)
  ) {
    return { level: 3, titre: t.replace(/\s*:\s*$/, "") };
  }
  return null;
}

/** Decoupe le texte en blocs structures. */
function parseBlocks(text) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (!l.trim()) {
      i++;
      continue;
    }
    if (/^\s*(-{3,}|_{3,}|={3,})\s*$/.test(l)) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }
    if (isTableRow(l)) {
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        if (!isSep(lines[i])) rows.push(splitRow(lines[i]));
        i++;
      }
      if (rows.length) blocks.push({ type: "table", rows });
      continue;
    }
    const h = headingLevel(l);
    if (h) {
      blocks.push({ type: "h", ...h });
      i++;
      continue;
    }
    if (isBullet(l) || isSubBullet(l)) {
      const items = [];
      while (i < lines.length && (isBullet(lines[i]) || isSubBullet(lines[i]) || !lines[i].trim())) {
        if (!lines[i].trim()) {
          if (i + 1 < lines.length && !isBullet(lines[i + 1]) && !isSubBullet(lines[i + 1])) break;
          i++;
          continue;
        }
        const txt = lines[i].replace(/^\s*([-•*]|\d+[.)]|[.·])\s+/, "");
        if (!videHorsSources(txt)) {
          items.push({ sub: isSubBullet(lines[i]) && !isBullet(lines[i].trim()), text: txt });
        }
        i++;
      }
      if (items.length) blocks.push({ type: "ul", items });
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !isTableRow(lines[i]) && !isBullet(lines[i]) && !headingLevel(lines[i])) {
      para.push(lines[i].trim());
      i++;
    }
    const texte = para.filter((l) => !videHorsSources(l)).join("\n");
    if (texte.trim()) blocks.push({ type: "p", text: texte });
  }
  return blocks;
}

/** Regroupe les blocs en sections, les titres remarquables devenant des encadres. */
function groupSections(blocks) {
  const out = [];
  let current = null;
  for (const b of blocks) {
    if (b.type === "h") {
      const tone = calloutTone(b.titre);
      if (tone) {
        current = { type: "callout", tone, titre: b.titre, children: [] };
        out.push(current);
        continue;
      }
      current = null;
      out.push(b);
      continue;
    }
    if (current) current.children.push(b);
    else out.push(b);
  }
  return out;
}

function Block({ b, k, onSourceClick }) {
  if (b.type === "hr") return <hr className="rm-hr" />;
  if (b.type === "h") {
    const Tag = b.level <= 2 ? "h3" : "h4";
    return <Tag className={`rm-h rm-h${b.level}`}>{inline(b.titre, k, onSourceClick)}</Tag>;
  }
  if (b.type === "ul") {
    return (
      <ul className="rm-ul">
        {b.items.map((it, j) => (
          <li key={j} className={it.sub ? "rm-li-sub" : ""}>
            {inline(it.text, `${k}-${j}`, onSourceClick)}
          </li>
        ))}
      </ul>
    );
  }
  if (b.type === "table") {
    const [head, ...body] = b.rows;
    // construit en createElement : le JSX mappe se fait envelopper par l'instrumentation
    // de l'editeur visuel (span display:contents), ce qui est invalide dans un <tr>.
    const th = head.map((c, j) =>
      createElement("th", { key: j }, inline(c, `${k}-th-${j}`, onSourceClick))
    );
    const trs = body.map((r, ri) =>
      createElement(
        "tr",
        { key: ri },
        r.map((c, ci) =>
          createElement(
            "td",
            { key: ci, className: ci === 0 ? "rm-td-first" : undefined },
            inline(c, `${k}-${ri}-${ci}`, onSourceClick)
          )
        )
      )
    );
    return createElement(
      "div",
      { className: "rm-table-wrap" },
      createElement(
        "table",
        { className: "rm-table" },
        createElement("thead", null, createElement("tr", null, th)),
        createElement("tbody", null, trs)
      )
    );
  }
  // paragraphe : la ligne "VERDICT : GO" devient une pastille
  const v = b.text.match(/^VERDICT\s*:?\s*([^\n]+)$/i);
  if (v) {
    const val = v[1].trim();
    const tone = (VERDICTS.find((x) => x.re.test(val)) || { tone: "warn" }).tone;
    return (
      <p className="rm-verdict-line">
        <span className="rm-verdict-label">Verdict</span>
        <span className={`rm-verdict rm-verdict-${tone}`}>{val}</span>
      </p>
    );
  }
  // on preserve les retours a la ligne voulus par l'agent
  const lignes = b.text.split("\n");
  return (
    <p className="rm-p">
      {lignes.map((ln, j) => (
        <span key={j}>
          {j > 0 && <br />}
          {inline(ln, `${k}-${j}`, onSourceClick)}
        </span>
      ))}
    </p>
  );
}

export default function RichMessage({ text, streaming, index = 0, collapseAt = 2600 }) {
  const [open, setOpen] = useState(false);
  const [srcOpen, setSrcOpen] = useState(false);
  // le repli sert a eviter le mur de texte sur telephone : inutile sur grand ecran
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const cb = (e) => setMobile(e.matches);
    mq.addEventListener("change", cb);
    return () => mq.removeEventListener("change", cb);
  }, []);

  if (!text || !text.trim()) return null;
  const { clean, sources } = extractSources(text);
  const blocks = groupSections(parseBlocks(clean));
  const tropLong = mobile && !streaming && clean.length > collapseAt;
  const replie = tropLong && !open;

  const render = (list, prefix) =>
    list.map((b, i) =>
      b.type === "callout" ? (
        <div key={`${prefix}-c${i}`} className={`rm-callout rm-callout-${b.tone}`}>
          <div className="rm-callout-title">{b.titre}</div>
          {render(b.children, `${prefix}-c${i}`)}
        </div>
      ) : (
        <Block key={`${prefix}-${i}`} b={b} k={`${prefix}-${i}`} onSourceClick={() => setSrcOpen(true)} />
      )
    );

  return (
    <div className="rm-root" data-testid={`rich-message-${index}`}>
      <div className={`rm-content ${replie ? "rm-collapsed" : ""}`}>
        {render(blocks, `b${index}`)}
        {streaming && <span className="chat-streaming-cursor" data-testid="chat-streaming-cursor" />}
      </div>

      {tropLong && (
        <button
          type="button"
          className="rm-more"
          onClick={() => setOpen(!open)}
          data-testid={`rich-message-toggle-${index}`}
        >
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {open ? "Replier" : "Voir la suite"}
        </button>
      )}

      {sources.length > 0 && (
        <div className="rm-sources" data-testid={`rich-message-sources-${index}`}>
          <button
            type="button"
            className="rm-sources-toggle"
            onClick={() => setSrcOpen(!srcOpen)}
            data-testid={`rich-message-sources-toggle-${index}`}
          >
            <FileBadge className="w-3.5 h-3.5" />
            {sources.length} source{sources.length > 1 ? "s" : ""} verifiable{sources.length > 1 ? "s" : ""}
            {srcOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {srcOpen && (
            <ol className="rm-sources-list">
              {sources.map((s, i) => (
                <li key={i}>
                  <span className="rm-src-num">{i + 1}</span>
                  <span>{inline(s, `src-${i}`, () => {})}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <style>{`
        .rm-root { font-family: var(--sans); color: var(--ink); }
        .rm-content { position: relative; }
        .rm-collapsed {
          max-height: 30rem;
          overflow: hidden;
          mask-image: linear-gradient(180deg, #000 72%, transparent 100%);
          -webkit-mask-image: linear-gradient(180deg, #000 72%, transparent 100%);
        }
        .rm-p { margin: 0 0 0.7rem; line-height: 1.62; font-size: 0.95rem; overflow-wrap: anywhere; }
        .rm-b { font-weight: 700; color: var(--ink); }
        .rm-num { font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
        .rm-link { color: var(--teal, #1F6F6B); text-decoration: underline; text-decoration-style: dotted; overflow-wrap: anywhere; }
        .rm-h { font-family: var(--serif); color: var(--ink); margin: 1.1rem 0 0.5rem; line-height: 1.25; }
        .rm-h2 { font-size: 1.15rem; font-weight: 600; }
        .rm-h3 {
          font-family: var(--sans); font-size: 0.72rem; font-weight: 700;
          letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold-dark, #8a6a38);
          border-bottom: 1px solid var(--line); padding-bottom: 0.3rem;
        }
        .rm-h4 { font-size: 0.98rem; font-weight: 600; }
        .rm-hr { border: none; border-top: 1px solid var(--line); margin: 1rem 0; }
        .rm-ul { margin: 0 0 0.75rem; padding: 0; list-style: none; }
        .rm-ul li {
          position: relative; padding-left: 1.05rem; margin-bottom: 0.4rem;
          line-height: 1.58; font-size: 0.94rem; overflow-wrap: anywhere;
        }
        .rm-ul li::before {
          content: ""; position: absolute; left: 0; top: 0.62em;
          width: 5px; height: 5px; border-radius: 1px; background: var(--gold);
        }
        .rm-ul li.rm-li-sub { padding-left: 2rem; font-size: 0.9rem; color: var(--ink-soft); }
        .rm-ul li.rm-li-sub::before { left: 1rem; background: var(--line); }

        .rm-est {
          background: rgba(214, 158, 46, 0.18); color: #7a5a12; padding: 0.05em 0.4em;
          border-radius: 3px; font-size: 0.78rem; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.04em; white-space: nowrap;
        }
        .rm-src-ref {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 1.05rem; height: 1.05rem; padding: 0 0.22rem; margin: 0 0.12rem;
          vertical-align: super; font-size: 0.6rem; font-weight: 700; line-height: 1;
          color: var(--teal, #1F6F6B); background: rgba(31, 111, 107, 0.1);
          border: 1px solid rgba(31, 111, 107, 0.25); border-radius: 3px; cursor: pointer;
        }
        .rm-src-ref:hover { background: rgba(31, 111, 107, 0.2); }

        .rm-verdict-line { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; margin: 0.6rem 0 0.9rem; }
        .rm-verdict-label {
          font-size: 0.64rem; font-weight: 700; letter-spacing: 0.18em;
          text-transform: uppercase; color: var(--muted);
        }
        .rm-verdict {
          display: inline-block; padding: 0.28rem 0.7rem; border-radius: 999px;
          font-size: 0.8rem; font-weight: 700; letter-spacing: 0.02em; border: 1px solid;
        }
        .rm-verdict-ok { background: rgba(31,111,107,0.12); color: #145f5b; border-color: rgba(31,111,107,0.4); }
        .rm-verdict-warn { background: rgba(214,158,46,0.16); color: #7a5a12; border-color: rgba(214,158,46,0.45); }
        .rm-verdict-bad { background: rgba(176,58,46,0.12); color: #8f2f26; border-color: rgba(176,58,46,0.35); }

        .rm-callout {
          border: 1px solid var(--line); border-left: 3px solid var(--gold);
          background: #fff; border-radius: 6px; padding: 0.85rem 1rem 0.6rem;
          margin: 0.9rem 0;
        }
        .rm-callout-title {
          font-size: 0.68rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
          color: var(--gold-dark, #8a6a38); margin-bottom: 0.5rem;
        }
        .rm-callout .rm-p:last-child, .rm-callout .rm-ul:last-child { margin-bottom: 0; }
        .rm-callout-terrain { border-left-color: var(--teal, #1F6F6B); background: rgba(31,111,107,0.04); }
        .rm-callout-terrain .rm-callout-title { color: var(--teal, #1F6F6B); }
        .rm-callout-equipe { border-left-color: var(--ink); background: var(--paper-2, #f6f2ea); }
        .rm-callout-equipe .rm-callout-title { color: var(--ink); }
        .rm-callout-alerte { border-left-color: #b03a2e; background: rgba(176,58,46,0.04); }
        .rm-callout-alerte .rm-callout-title { color: #8f2f26; }
        .rm-callout-controle { border-left-color: #d69e2e; background: rgba(214,158,46,0.06); }
        .rm-callout-controle .rm-callout-title { color: #7a5a12; }

        .rm-table-wrap { overflow-x: auto; margin: 0.6rem 0 1rem; -webkit-overflow-scrolling: touch; }
        .rm-table { width: 100%; min-width: 22rem; border-collapse: collapse; font-size: 0.86rem; }
        .rm-table th {
          background: var(--ink); color: #faf7f1; text-align: left; font-weight: 600;
          padding: 0.5rem 0.65rem; font-size: 0.72rem; letter-spacing: 0.06em; text-transform: uppercase;
        }
        .rm-table td { padding: 0.5rem 0.65rem; border-bottom: 1px solid var(--line); vertical-align: top; }
        .rm-table tbody tr:nth-child(even) { background: var(--paper-2, #f6f2ea); }
        .rm-td-first { font-weight: 600; }

        .rm-more {
          display: inline-flex; align-items: center; gap: 0.35rem; margin-top: 0.2rem;
          font-size: 0.78rem; font-weight: 600; color: var(--gold-dark, #8a6a38);
          background: transparent; border: none; padding: 0.35rem 0; cursor: pointer;
        }
        .rm-sources { margin-top: 0.7rem; border-top: 1px dashed var(--line); padding-top: 0.5rem; }
        .rm-sources-toggle {
          display: inline-flex; align-items: center; gap: 0.35rem; background: transparent;
          border: none; padding: 0; cursor: pointer; font-size: 0.72rem; font-weight: 600;
          color: var(--teal, #1F6F6B); letter-spacing: 0.02em;
        }
        .rm-sources-list { margin: 0.5rem 0 0; padding: 0; list-style: none; }
        .rm-sources-list li {
          display: flex; gap: 0.45rem; font-size: 0.76rem; line-height: 1.45;
          color: var(--ink-soft); margin-bottom: 0.35rem; overflow-wrap: anywhere;
        }
        .rm-src-num {
          flex: none; min-width: 1rem; height: 1rem; display: inline-flex; align-items: center;
          justify-content: center; font-size: 0.6rem; font-weight: 700; border-radius: 3px;
          color: var(--teal, #1F6F6B); background: rgba(31,111,107,0.1);
        }

        @media (max-width: 640px) {
          .rm-p, .rm-ul li { font-size: 0.92rem; }
          .rm-collapsed { max-height: 22rem; }
          .rm-table { font-size: 0.8rem; min-width: 18rem; }
          .rm-table th, .rm-table td { padding: 0.4rem 0.5rem; }
          .rm-callout { padding: 0.7rem 0.75rem 0.5rem; }
        }
      `}</style>
    </div>
  );
}
